import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, ShieldCheck } from 'lucide-react';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ERR = {
  no_code: ['Aucun code actif, demandez-en un nouveau.', 'No active code, request a new one.'],
  wrong_code: ['Code incorrect, réessayez.', 'Wrong code, try again.'],
  expired: ['Code expiré, demandez-en un nouveau.', 'Code expired, request a new one.'],
  too_many_attempts: ['Trop d’essais. Continuez sans vérification.', 'Too many attempts. Continue without verification.'],
  wait: ['Patientez 30 secondes avant de renvoyer.', 'Wait 30 seconds before resending.'],
  too_many: ['Limite de renvois atteinte.', 'Resend limit reached.'],
};

export const PhoneCodeStep = ({ phone, en, onVerified, onSkip, busy }) => {
  const [code, setCode] = useState('');
  const [state, setState] = useState('idle');
  const [msg, setMsg] = useState('');
  const err = (d) => (ERR[d] ? ERR[d][en ? 1 : 0] : (en ? 'Something went wrong.' : 'Une erreur est survenue.'));

  const verify = async (e) => {
    e.preventDefault();
    setState('checking');
    try {
      const res = await axios.post(`${API}/phone/verify`, { phone, code });
      onVerified(res.data.token);
    } catch (ex) {
      setState('idle');
      setMsg(err(ex.response?.data?.detail));
    }
  };

  const resend = async () => {
    setMsg('');
    try {
      await axios.post(`${API}/phone/send-code`, { phone });
      setMsg(en ? 'New code sent.' : 'Nouveau code envoyé.');
    } catch (ex) {
      setMsg(err(ex.response?.data?.detail));
    }
  };

  return (
    <form onSubmit={verify} className="space-y-3 animate-fade-up" data-testid="phone-code-step">
      <div className="flex items-start gap-3 bg-slate-50 rounded-xl p-3">
        <ShieldCheck className="w-5 h-5 text-[#1a5c3b] shrink-0 mt-0.5" />
        <p className="text-sm text-slate-600">
          {en ? 'We sent a 6-digit code by SMS to ' : 'Nous avons envoyé un code à 6 chiffres par SMS au '}
          <span className="font-bold text-slate-900">{phone}</span>
        </p>
      </div>
      <Input
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        required
        value={code}
        onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setMsg(''); }}
        placeholder="••••••"
        className="h-12 text-center text-2xl tracking-[0.5em] font-bold rounded-xl bg-gray-50"
        data-testid="phone-code-input"
      />
      {msg && <p className="text-sm text-center text-slate-600" data-testid="phone-code-message">{msg}</p>}
      <button type="submit" disabled={code.length !== 6 || state === 'checking' || busy} className="w-full h-12 rounded-xl bg-[#1a5c3b] hover:bg-[#165432] text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-colors" data-testid="phone-code-submit">
        {(state === 'checking' || busy) && <Loader2 className="w-4 h-4 animate-spin" />}
        {en ? 'Confirm and send my request' : 'Confirmer et envoyer ma demande'}
      </button>
      <div className="flex items-center justify-between text-xs">
        <button type="button" onClick={resend} className="text-[#1a5c3b] font-semibold hover:underline" data-testid="phone-code-resend">{en ? 'Resend code' : 'Renvoyer le code'}</button>
        <button type="button" onClick={onSkip} disabled={busy} className="text-slate-400 hover:text-slate-600 hover:underline" data-testid="phone-code-skip">{en ? 'Didn’t get it? Send anyway' : 'Pas reçu? Envoyer quand même'}</button>
      </div>
    </form>
  );
};
